import { useState } from 'react';
import { toast } from 'sonner';
import { admin, apiErrorMessage, type AdminCredentialItem } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from '../../i18n';
import { DataTable, Button, Modal, Tabs, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

const TIERS = ['micro', 'standard', 'major', 'master', 'legendary'];

function handle(id: string) {
  return `#${id.replace(/-/g, '').slice(-4).toUpperCase()}`;
}

export default function AdminCredentialsPage() {
  const t = useAdminT();
  const { t: gt } = useTranslation();
  const [tier, setTier] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [revoking, setRevoking] = useState<AdminCredentialItem | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const list = useApi(
    () => admin.credentials(tier === 'all' ? undefined : tier),
    [tier, refreshKey],
  );
  const rows = list.status === 'success' ? list.data : [];

  async function confirmRevoke() {
    if (!revoking) return;
    if (!reason.trim()) { toast.error(t('reason')); return; }
    setBusy(true);
    try {
      await admin.revokeCredential(revoking.id, reason.trim());
      toast.success(t('credentials.revoked'));
      setRevoking(null); setReason('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<AdminCredentialItem>[] = [
    { key: 'credential_urn', header: t('col.urn'), mono: true, width: '180px',
      render: (r) => r.credential_urn.slice(-16) },
    { key: 'tier', header: t('col.tier'), width: '110px' },
    { key: 'credential_type', header: t('col.type') },
    { key: 'user_id', header: t('col.user'), width: '96px', mono: true, render: (r) => handle(r.user_id) },
    { key: 'issued_at', header: t('col.issued'), width: '120px', mono: true,
      render: (r) => (r.issued_at ? r.issued_at.slice(0, 10) : '—') },
    { key: 'actions', header: t('col.actions'), width: '90px', align: 'right',
      render: (r) => (
        <Button size="sm" variant="link" onClick={() => { setRevoking(r); setReason(''); }}>
          {t('action.revoke')}
        </Button>
      ) },
  ];

  const tabs = [{ key: 'all', label: t('credentials.allTiers') }, ...TIERS.map((x) => ({ key: x, label: x }))];

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('credentials.title')}</h1>
        <p className="admin-page__sub">{t('credentials.subtitle')}</p>
      </header>

      <div className="admin-toolbar">
        <Tabs items={tabs} value={tier} onChange={setTier} aria-label={t('col.tier')} />
      </div>

      <DataTable columns={columns} rows={rows} rowKey={(r) => r.id}
        loading={list.status === 'loading'} empty={t('emptyGeneric')} />

      <Modal
        open={revoking != null}
        onOpenChange={(o) => { if (!o) setRevoking(null); }}
        title={t('credentials.revokeTitle')}
        description={t('credentials.revokeConfirm')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setRevoking(null)}>{t('action.cancel')}</Button>
            <Button variant="danger" size="sm" disabled={busy} onClick={confirmRevoke}>{t('action.revoke')}</Button>
          </>
        }
      >
        <label className="admin-field">
          <span className="admin-field__label">{t('reason')}</span>
          <textarea className="prop-textarea" rows={2} value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </label>
      </Modal>
    </div>
  );
}
